import axios, { AxiosInstance } from "axios";
import { API_BASE_URL } from "../apiservices/apiService";
import {
  NETWORK_MESSAGES,
  GET_REQUEST_TIMEOUT_MS,
  getClientSideErrorCode,
  getSessionErrorMessage,
  isOffline,
} from "./networkErrors";

const ApiService: AxiosInstance = axios.create({
  // baseURL: "http://165.22.219.69:5002",
  baseURL: `${API_BASE_URL}`,

  // baseURL: "http://165.22.219.69:5002",
  // timeout: 10000, // optional timeout
  headers: {
    "Content-Type": "multipart/form-data", // Set default headers if needed
  },
});

// Shown when the server didn't send a usable message of its own. Session
// statuses (401/440) are still passed through as-is, since every page checks
// `response.status === 440 || 401` to log out and redirect to login.
const FRIENDLY_STATUS_MESSAGES: Record<number, string> = {
  400: "Something in this request wasn't right. Please check and try again.",
  401: "Your session has ended. Please log in again.",
  403: "You do not have permission to access this section.",
  404: "Sorry, the data you are looking for could not be found.",
  413: "The file is too large to upload.",
  429: "Too many requests. Please wait a moment and try again.",
  440: "Your session has expired. Please log in again.",
  500: "Something went wrong on our side. Please try again in a moment.",
  502: "The server is temporarily unavailable. Please try again in a moment.",
  503: "The server is temporarily unavailable. Please try again in a moment.",
  504: "The server took too long to respond. Please try again.",
};

// Raw library/stack messages that used to reach the screen verbatim
// (e.g. "invalid signature", "jwt malformed", "Cannot read properties of
// null") - never show these to a user.
const isTechnicalMessage = (message: string) =>
  /jwt|signature|token|cast to objectid|cannot read prop|undefined|null|econn|mongo|stack/i.test(
    message
  );

const pickMessage = (status: number, data: any): string => {
  const serverMessage =
    data && typeof data === "object" && typeof data.message === "string"
      ? data.message.trim()
      : "";
  if (serverMessage && !isTechnicalMessage(serverMessage)) return serverMessage;
  return (
    FRIENDLY_STATUS_MESSAGES[status] ||
    (status >= 500
      ? FRIENDLY_STATUS_MESSAGES[500]
      : "Something went wrong. Please try again.")
  );
};

ApiService.interceptors.request.use((config) => {
  // Fail fast with a clear message instead of waiting on a request that
  // can't go anywhere.
  if (isOffline()) {
    return Promise.reject({ code: "OFFLINE", isOfflineCheck: true });
  }
  if ((config.method || "get").toLowerCase() === "get" && !config.timeout) {
    config.timeout = GET_REQUEST_TIMEOUT_MS;
  }
  return config;
});

ApiService.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // The server responded with a non-2xx status.
      const { status, data } = error.response;
      const message = pickMessage(status, data);
      return Promise.reject({
        status,
        code: data?.code,
        action: data?.action,
        message:
          status === 401 || status === 440
            ? getSessionErrorMessage(message)
            : message,
        data,
      });
    }

    // No response at all - offline, slow/weak network, timeout, request
    // blocked by a VPN/firewall/ad-blocker, or cancelled. These are on the
    // user's side, so the message tells them what to check.
    const code = error?.isOfflineCheck ? "OFFLINE" : getClientSideErrorCode(error);
    return Promise.reject({
      status: 0,
      code,
      message: NETWORK_MESSAGES[code],
    });
  }
);

export default ApiService;
